import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { ExternalLink } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { SeverityBadge } from '@/components/shared/SeverityBadge'
import { api } from '@/lib/api'
import type { Severity } from '@/lib/types'

/**
 * Supply-chain incidents that reference a given CVE (Plan C Plan 2 T20).
 *
 * Wraps `GET /api/supply-chain/by-cve/:cveId` which returns the compact
 * `IncidentForCve[]` shape from `projectIncidentsForCve()`. The shape is
 * intentionally narrower than the public `/api/supply-chain/incidents`
 * list — only the fields needed for the CVE detail cross-ref panel.
 *
 * Self-hides on empty + error so the CVE detail page does not get noise
 * for CVEs without supply-chain incidents (the common case).
 *
 * Spec: §5.4 of the supply-chain v1 design doc.
 */

export interface IncidentForCve {
  id: string
  providerSlug: string
  providerName: string
  source: string
  title: string
  url: string
  publishedAt: string
  severityNormalised: 'low' | 'medium' | 'high' | 'critical' | null
  isSecurityRelevant: boolean
}

export interface ByCveResponse {
  incidents: IncidentForCve[]
}

/**
 * Shared query options for supply-chain incidents by CVE. Both the card
 * below and the Vulnerability Detail page's vendor-display fallback chain
 * subscribe through this so a single network round-trip serves both.
 */
export function supplyChainByCveQueryOptions(cveId: string) {
  return {
    queryKey: ['supply-chain', 'by-cve', cveId] as const,
    queryFn: () => api.get<ByCveResponse>(`/supply-chain/by-cve/${cveId}`),
    enabled: Boolean(cveId),
    staleTime: 60_000,
    retry: false,
  }
}

interface Props {
  cveId: string
}

export function SupplyChainIncidentsForCveCard({ cveId }: Props) {
  const { data, isLoading, isError } = useQuery(
    supplyChainByCveQueryOptions(cveId),
  )

  // Self-hide on error/empty so the CVE detail page stays clean for CVEs
  // with no supply-chain cross-references (the common case).
  if (isLoading) {
    return (
      <Card aria-labelledby="supply-chain-for-cve-card-title">
        <CardHeader>
          <CardTitle
            id="supply-chain-for-cve-card-title"
            className="text-base"
          >
            Supply-chain incidents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </CardContent>
      </Card>
    )
  }

  if (isError || !data || data.incidents.length === 0) return null

  return (
    <Card aria-labelledby="supply-chain-for-cve-card-title">
      <CardHeader>
        <CardTitle id="supply-chain-for-cve-card-title" className="text-base">
          Supply-chain incidents ({data.incidents.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {data.incidents.map((i) => (
          <div
            key={i.id}
            className="space-y-1 rounded-md border bg-card p-3"
          >
            <div className="flex items-start justify-between gap-2">
              <Link
                to={`/providers/${i.providerSlug}`}
                className="text-sm font-semibold hover:underline"
              >
                {i.providerName}
              </Link>
              <div className="flex flex-wrap items-center gap-1.5">
                {i.severityNormalised && (
                  <SeverityBadge severity={i.severityNormalised as Severity} />
                )}
                {i.isSecurityRelevant && (
                  <Badge variant="outline" className="text-xs">
                    security
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">
                  {i.source}
                </Badge>
              </div>
            </div>
            <p className="text-sm">{i.title}</p>
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>
                {formatDistanceToNow(new Date(i.publishedAt), {
                  addSuffix: true,
                })}
              </span>
              <a
                href={i.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 underline hover:text-foreground"
                // R28: incident headlines name victim organisations, in an attribute.
                data-ph-no-capture
                aria-label={`Open ${i.title} on upstream source`}
              >
                Source <ExternalLink className="h-3 w-3" aria-hidden />
              </a>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
